import type { SiteCopy } from "@tih/shared";
import { getAtPath, setAtPath } from "../../lib/siteCopyPaths";
import type { CopyArrayBlockDef, CopyFieldDef, CopySectionDef } from "../../lib/adminCopySections";
import { AdminImageUploadField } from "./AdminImageUploadField";

export function AdminCopyField({
  field,
  merged,
  patch,
  setPatch,
}: {
  field: CopyFieldDef;
  merged: SiteCopy;
  patch: Record<string, unknown>;
  setPatch: (p: Record<string, unknown>) => void;
}) {
  const val = String(getAtPath(merged, field.path) ?? "");
  const onChange = (nextVal: string) => {
    const n = structuredClone(patch);
    setAtPath(n, field.path, nextVal);
    setPatch(n);
  };

  return (
    <div
      className={`admin-field admin-copy-field${field.wide ? " admin-copy-field--wide" : ""}`}
    >
      {field.image ? (
        <AdminImageUploadField
          label={field.label}
          hint={field.hint}
          value={val}
          onChange={onChange}
          formIdentifier={`admin-copy-${field.path.replace(/\./g, "-")}`}
        />
      ) : (
        <label>
          <span>{field.label}</span>
          {field.multiline ? (
            <textarea
              className="admin-content-editor"
              rows={field.wide ? 4 : 3}
              value={val}
              onChange={(e) => onChange(e.target.value)}
              spellCheck
            />
          ) : (
            <input type="text" value={val} onChange={(e) => onChange(e.target.value)} spellCheck />
          )}
          {field.hint && <span className="admin-copy-field-hint">{field.hint}</span>}
        </label>
      )}
    </div>
  );
}

function AdminCopyArrayBlock({
  block,
  merged,
  patch,
  setPatch,
}: {
  block: CopyArrayBlockDef;
  merged: SiteCopy;
  patch: Record<string, unknown>;
  setPatch: (p: Record<string, unknown>) => void;
}) {
  return (
    <div className="admin-copy-array-item">
      <div className="admin-copy-array-head">
        <h3 className="admin-copy-array-title">{block.title}</h3>
        {block.hint && <p className="admin-copy-array-hint">{block.hint}</p>}
      </div>
      <div className="admin-copy-field-grid">
        {block.fields.map((field) => (
          <AdminCopyField
            key={field.path}
            field={field}
            merged={merged}
            patch={patch}
            setPatch={setPatch}
          />
        ))}
      </div>
    </div>
  );
}

export function AdminCopySection({
  section,
  merged,
  patch,
  setPatch,
}: {
  section: CopySectionDef;
  merged: SiteCopy;
  patch: Record<string, unknown>;
  setPatch: (p: Record<string, unknown>) => void;
}) {
  return (
    <section className="admin-card admin-copy-section">
      <div className="admin-copy-section-head">
        <div>
          <h2 className="admin-card-title">{section.title}</h2>
          {section.description && <p className="admin-card-sub">{section.description}</p>}
        </div>
        {section.previewHref && (
          <a
            href={section.previewHref}
            target="_blank"
            rel="noopener noreferrer"
            className="admin-copy-preview-link"
          >
            Preview ↗
          </a>
        )}
      </div>

      {section.fields && section.fields.length > 0 && (
        <div className="admin-copy-field-grid">
          {section.fields.map((field) => (
            <AdminCopyField
              key={field.path}
              field={field}
              merged={merged}
              patch={patch}
              setPatch={setPatch}
            />
          ))}
        </div>
      )}

      {section.arrays?.map((block) => (
        <AdminCopyArrayBlock
          key={`${block.basePath}.${block.index}`}
          block={block}
          merged={merged}
          patch={patch}
          setPatch={setPatch}
        />
      ))}
    </section>
  );
}
